import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import {IStatusPageView} from 'entities/statusView';

import httpBaseQuery from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IUserReport, {IUserReportOutput} from '../model/UserReport/IUserReport';
import CreateUserReportType from '../model/UserReport/CreateUserReportType';
import UpdateUserReportType from '../model/UserReport/UpdateUserReportType';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import {buildQuery} from '../../../shared/api/query';

export const userReportAPI = createApi({
    reducerPath: 'userReportAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['UserReport'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getUserReports: build.query<IPagination<IUserReport>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.USER_REPORTS(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'UserReport'),
            transformResponse(
                rawResult: IHydraResponse<IUserReport>,
            ): Promise<IPagination<IUserReport>> | IPagination<IUserReport> {
                return paginationAdapter(rawResult);
            },
        }),
        getUserReport: build.query<IUserReportOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.USER_REPORT(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'UserReport', id: uuid }],
        }),
        getUserReportView: build.query<IStatusPageView, string>({
            query: (uuid) => ({
                path: ApiUrls.USER_REPORT_VIEW(uuid),
                optionalAuth: true,
            }),
        }),
        addUserReport: build.mutation<IUserReportOutput, CreateUserReportType>({
            query: (data) => ({
                path: ApiUrls.USER_REPORTS(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'UserReport', id: 'LIST' }],
        }),
        updateUserReport: build.mutation<IUserReportOutput, UpdateUserReportType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.USER_REPORT(uuid),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ uuid, ...patch }, { dispatch }) {
                dispatch(
                    userReportAPI.util.updateQueryData('getUserReport', uuid, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: (result, error, {uuid}) => [
                { type: 'UserReport', id: uuid },
                { type: 'UserReport', id: 'LIST' },
            ],
        }),
        deleteUserReport: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.USER_REPORT(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'UserReport', id: 'LIST' }],
        }),
    }),
});
